import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { AiProviderSettingInputSchema, type AiProviderSettingInput } from '@app/shared';
import {
  useAiProviderSetting,
  useTestAiProvider,
  useUpdateAiProvider,
} from './aiProviderApi';
import { getApiErrorMessage } from '../../lib/api-client';

export function AiProviderForm(): JSX.Element {
  const { data, isLoading, isError, error } = useAiProviderSetting();
  const updateMutation = useUpdateAiProvider();
  const testMutation = useTestAiProvider();
  const [savedFlash, setSavedFlash] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<AiProviderSettingInput>({
    resolver: zodResolver(AiProviderSettingInputSchema),
    defaultValues: {
      enabled: false,
      baseUrl: '',
      apiKey: '',
      model: 'gpt-4o-mini',
      temperature: 0.2,
      maxTokens: 2048,
      dailyCostUsd: 5,
    },
  });

  useEffect(() => {
    if (data) {
      reset({
        enabled: data.enabled,
        baseUrl: data.baseUrl,
        apiKey: '',
        model: data.model,
        temperature: data.temperature,
        maxTokens: data.maxTokens,
        dailyCostUsd: data.dailyCostUsd,
      });
    }
  }, [data, reset]);

  if (isLoading) return <div role="status" className="text-slate-500">載入中...</div>;
  if (isError) {
    return (
      <div role="alert" className="text-red-600">
        載入失敗：{getApiErrorMessage(error, '未知錯誤')}
      </div>
    );
  }

  const onSubmit = (values: AiProviderSettingInput): void => {
    setSavedFlash(null);
    updateMutation.mutate(values, {
      onSuccess: () => {
        setSavedFlash('已儲存');
        reset({ ...values, apiKey: '' });
      },
    });
  };

  const errMsg = (e: unknown): string => getApiErrorMessage(e, '未知錯誤');

  return (
    <section className="grid gap-4 max-w-xl">
      <h2 className="text-lg font-semibold">AI 提供商</h2>
      <p className="text-sm text-slate-600">
        透過 OpenAI 相容 API 呼叫 LLM（OpenAI / Gemini / Claude 皆可）。API Key 以 AES-256-GCM 加密儲存;留空代表保留原 Key。
      </p>

      <form onSubmit={handleSubmit(onSubmit)} className="grid gap-3 bg-white border rounded p-4">
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" {...register('enabled')} /> 啟用 AI 功能
        </label>

        <Row label="Base URL" error={errors.baseUrl?.message}>
          <input
            {...register('baseUrl')}
            className={inputCls}
            placeholder="https://litellm.example.com/v1"
          />
        </Row>

        <Row
          label={`API Key${data?.hasApiKey ? '（已設定，留空保留原值）' : ''}`}
          error={errors.apiKey?.message}
        >
          <input
            type="password"
            {...register('apiKey')}
            className={inputCls}
            autoComplete="new-password"
            placeholder={data?.hasApiKey ? '（留空保留原 Key）' : '（未設定）'}
          />
        </Row>

        <Row label="模型" error={errors.model?.message}>
          <input {...register('model')} className={inputCls} placeholder="gpt-4o-mini" />
        </Row>

        <Row label="Temperature" error={errors.temperature?.message}>
          <input
            type="number"
            step="0.1"
            min="0"
            max="2"
            {...register('temperature', { valueAsNumber: true })}
            className={`${inputCls} w-32`}
          />
        </Row>

        <Row label="Max tokens" error={errors.maxTokens?.message}>
          <input
            type="number"
            min="64"
            max="16000"
            step="64"
            {...register('maxTokens', { valueAsNumber: true })}
            className={`${inputCls} w-32`}
          />
        </Row>

        <Row label="每日成本上限（USD）" error={errors.dailyCostUsd?.message}>
          <input
            type="number"
            step="0.01"
            min="0"
            {...register('dailyCostUsd', { valueAsNumber: true })}
            className={`${inputCls} w-32`}
          />
        </Row>

        {updateMutation.isError ? (
          <div role="alert" className="text-red-600 text-sm">儲存失敗：{errMsg(updateMutation.error)}</div>
        ) : null}
        {savedFlash ? (
          <div role="status" className="text-green-700 text-sm">{savedFlash}</div>
        ) : null}

        <div className="flex gap-2 items-center">
          <button
            type="submit"
            disabled={updateMutation.isPending}
            className="bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {updateMutation.isPending ? '儲存中…' : '儲存'}
          </button>
          <button
            type="button"
            disabled={testMutation.isPending || isDirty}
            title={isDirty ? '請先儲存再測試' : '送一句 ping 至 AI 提供商驗證連線'}
            onClick={() => testMutation.mutate()}
            className="border border-blue-600 text-blue-600 px-3 py-1.5 rounded hover:bg-blue-50 disabled:opacity-50"
          >
            {testMutation.isPending ? '測試中…' : '測試連線'}
          </button>
        </div>

        {testMutation.isSuccess && testMutation.data ? (
          testMutation.data.ok ? (
            <div role="status" className="text-green-700 text-sm">
              ✓ 連線成功（{testMutation.data.model ?? '?'}，
              {testMutation.data.latencyMs ?? 0} ms，
              tokens in={testMutation.data.tokensIn ?? 0} / out={testMutation.data.tokensOut ?? 0}）
            </div>
          ) : (
            <div role="alert" className="text-red-600 text-sm">
              ✗ 連線失敗：{testMutation.data.message}
            </div>
          )
        ) : null}
        {testMutation.isError ? (
          <div role="alert" className="text-red-600 text-sm">測試失敗：{errMsg(testMutation.error)}</div>
        ) : null}
      </form>
    </section>
  );
}

function Row({
  label,
  error,
  children,
}: {
  label: string;
  error: string | undefined;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="grid gap-1 text-sm">
      <span className="text-slate-700">{label}</span>
      {children}
      {error ? <span role="alert" className="text-red-600 text-xs">{error}</span> : null}
    </label>
  );
}

const inputCls = 'border rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400';
